import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'

/**
 * Dragging a list row out of one band and into another.
 *
 * Written by hand, for the reason the dispatch board's gestures are (decision
 * 29) and one more that belongs to a list: the bands tile it, so the browser
 * already knows which one the pointer is inside and says so with `pointerover`.
 * A library that decides the target by comparing rectangles has to measure
 * every band on every frame, and in a list that collapses, filters and regroups
 * under it those rectangles go stale between one frame and the next. The hit
 * test here cannot disagree with what is under the cursor because it *is* what
 * is under the cursor: a band is anything carrying `data-band`, and a release
 * anywhere else drops nothing.
 *
 * Three things this owes the person doing it, which is what the previous
 * rectangle-matching version left out:
 *
 * - **Something has to move.** The row in flight is drawn at the pointer
 *   (`ghostRef`), positioned imperatively so a 120Hz pointermove is not forty
 *   rows of React.
 * - **The whole row is the grab.** A 16px column of grey dots is a target you
 *   have to aim at, and missing it looked exactly like the feature not
 *   existing. Controls inside the row keep their clicks: a pointer-down that
 *   lands on the team `Select` is never a drag.
 * - **A long list has to scroll.** The band somebody is aiming at is often off
 *   screen, so the nearest scrolling ancestor is nudged while the pointer sits
 *   near its edge.
 *
 * The gesture is a ref and only what is drawn is state. Below the slop nothing
 * happens at all, so a click on a row still opens the person — and `dragged()`
 * is how the row's own click handler knows the pointer-up it just saw ended a
 * drag rather than a click.
 *
 * **Where in the band is optional.** A band whose rows carry `data-row` also
 * reports the row a release would land above (`before`, null for the end of the
 * band), so the same gesture serves a list where order means something — the
 * catalog — and one where it does not. That reading measures exactly one
 * rectangle, the row the browser says the pointer is inside, which is what
 * keeps it honest in a list that collapses and filters underneath it. A band
 * with no `data-row` anywhere reports `before: null` and nothing changes for
 * the screens that came first.
 */

const DRAG_SLOP_PX = 6
const AUTOSCROLL_EDGE_PX = 72
const AUTOSCROLL_MAX_PX = 16

/**
 * A pointer-down inside one of these never becomes a drag.
 *
 * The team `Select` on the row is the keyboard path onto a team; opening it has
 * to stay a click even though the row around it is now a drag handle.
 */
const CONTROLS = 'select, option, input, textarea, a[href], [data-no-drag]'

interface Gesture {
  id: string
  from: string
  pointerId: number
  startX: number
  startY: number
  armed: boolean
  scroller: HTMLElement | null
}

export interface BandDrag {
  /** Pointer-down on a row. A drag past the slop, a click below it. */
  begin: (event: ReactPointerEvent, id: string, band: string) => void
  /** True while the click that follows a real drag is still to come. */
  dragged: () => boolean
  /** The row in flight, or null. */
  carrying: { id: string; from: string } | null
  /** The band a release would drop onto, origin band included. */
  over: string | null
  /**
   * The row a release would land above, or null for the end of the band.
   *
   * Always null unless the bands mark their rows with `data-row`.
   */
  before: string | null
  /** Attach to the element drawn at the pointer. */
  ghostRef: React.RefObject<HTMLDivElement | null>
}

/** How fast to scroll when the pointer is this far into an edge, and which way. */
function edgeVelocity(position: number, low: number, high: number): number {
  if (position < low + AUTOSCROLL_EDGE_PX) {
    const depth = Math.min(AUTOSCROLL_EDGE_PX, low + AUTOSCROLL_EDGE_PX - position)
    return -Math.round((depth / AUTOSCROLL_EDGE_PX) * AUTOSCROLL_MAX_PX)
  }

  if (position > high - AUTOSCROLL_EDGE_PX) {
    const depth = Math.min(AUTOSCROLL_EDGE_PX, position - (high - AUTOSCROLL_EDGE_PX))
    return Math.round((depth / AUTOSCROLL_EDGE_PX) * AUTOSCROLL_MAX_PX)
  }

  return 0
}

/**
 * The element that scrolls the list, found once at pointer-down.
 *
 * In this app it is the shell's `<main>`, but the hook is not told that: a
 * screen that grows its own scroller should not have to say so.
 */
function nearestScroller(from: HTMLElement | null): HTMLElement | null {
  for (let node = from; node; node = node.parentElement) {
    const overflow = getComputedStyle(node).overflowY
    const scrolls = overflow === 'auto' || overflow === 'scroll'
    if (scrolls && node.scrollHeight > node.clientHeight) return node
  }

  return null
}

export function useBandDrag({
  enabled,
  attribute = 'data-band',
  rowAttribute = 'data-row',
  onDrop,
}: {
  enabled: boolean
  /** The attribute a band marks itself with. Its value is the band's id. */
  attribute?: string
  /** The attribute a row marks itself with, for a list where order matters. */
  rowAttribute?: string
  /**
   * A row released over a band.
   *
   * Called for the origin band too, because a release there is a reorder in a
   * list that has an order. Screens that only care which band somebody is in
   * compare and return early.
   */
  onDrop: (id: string, band: string, before: string | null) => void
}): BandDrag {
  const gesture = useRef<Gesture | null>(null)
  const pointer = useRef({ x: 0, y: 0 })
  const target = useRef<string | null>(null)
  const insert = useRef<string | null>(null)
  const wasDrag = useRef(false)
  const frame = useRef<number | null>(null)
  const ghostRef = useRef<HTMLDivElement | null>(null)

  const [carrying, setCarrying] = useState<{ id: string; from: string } | null>(null)
  const [over, setOver] = useState<string | null>(null)
  const [before, setBefore] = useState<string | null>(null)

  const place = useCallback(() => {
    const ghost = ghostRef.current
    if (ghost) {
      ghost.style.transform = `translate3d(${pointer.current.x + 14}px, ${pointer.current.y + 12}px, 0)`
    }
  }, [])

  // The ghost is mounted a render after the gesture armed, so its first
  // position is set here rather than by the pointermove that armed it.
  useEffect(() => {
    if (carrying) place()
  }, [carrying, place])

  const finish = useCallback(
    (commit: boolean) => {
      const active = gesture.current
      const band = target.current
      const above = insert.current

      gesture.current = null
      target.current = null
      insert.current = null

      if (frame.current !== null) {
        cancelAnimationFrame(frame.current)
        frame.current = null
      }

      document.body.classList.remove('board-dragging')
      setCarrying(null)
      setOver(null)
      setBefore(null)

      if (!commit || !active?.armed) return
      if (!band) return

      onDrop(active.id, band, above)
    },
    [onDrop],
  )

  const step = useCallback(() => {
    frame.current = null

    const scroller = gesture.current?.scroller
    if (!scroller) return

    const rect = scroller.getBoundingClientRect()
    const dy = edgeVelocity(pointer.current.y, rect.top, rect.bottom)
    if (dy) scroller.scrollBy({ top: dy })

    frame.current = requestAnimationFrame(step)
  }, [])

  // One set of window listeners for the life of the screen rather than a set
  // per gesture, so a release outside the list still ends the drag.
  useEffect(() => {
    /**
     * The band under the pointer, straight from the browser.
     *
     * Read off the DOM rather than from a React handler per band, because
     * `onPointerEnter` and `onPointerLeave` are synthesised by React from
     * `pointerover` and `pointerout` and a gap between two bands is then a
     * band of its own. Anything that is not inside one clears the target, so
     * releasing over the toolbar drops nothing.
     */
    const aim = (node: EventTarget | null, y: number) => {
      if (!(node instanceof Element)) return

      const band = node.closest(`[${attribute}]`)?.getAttribute(attribute) ?? null
      target.current = band
      setOver(band)

      /**
       * Above or below the row the pointer is inside.
       *
       * One rectangle, of the element the browser already handed over. The
       * innermost `[data-row]` is the right one even where a group's whole
       * section is a row at the level above it, because the pointer being
       * inside a child means the child is what it is aiming at.
       */
      const row = band === null ? null : node.closest(`[${rowAttribute}]`)

      if (!row) {
        insert.current = null
        setBefore(null)
        return
      }

      const box = row.getBoundingClientRect()
      const after = y > box.top + box.height / 2
      const next = after ? row.nextElementSibling : row

      const above = next?.getAttribute(rowAttribute) ?? null
      insert.current = above
      setBefore(above)
    }

    const onPointerMove = (event: PointerEvent) => {
      const active = gesture.current
      if (!active || event.pointerId !== active.pointerId) return

      pointer.current = { x: event.clientX, y: event.clientY }

      if (!active.armed) {
        const travelled = Math.hypot(event.clientX - active.startX, event.clientY - active.startY)
        // Below the slop this is still a click, and a click opens the person.
        if (travelled < DRAG_SLOP_PX) return

        active.armed = true
        wasDrag.current = true
        document.body.classList.add('board-dragging')
        setCarrying({ id: active.id, from: active.from })
        if (frame.current === null) frame.current = requestAnimationFrame(step)
      }

      // Every move, not only the ones that cross into a new element: the caret
      // has to follow the hand down a tall row, and a drag that arms after
      // crossing into the next band has already missed that `pointerover`.
      aim(event.target, event.clientY)

      event.preventDefault()
      place()
    }

    const onPointerOver = (event: PointerEvent) => {
      if (!gesture.current?.armed) return
      aim(event.target, event.clientY)
    }

    const onPointerUp = (event: PointerEvent) => {
      if (!gesture.current || event.pointerId !== gesture.current.pointerId) return
      finish(true)
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && gesture.current) finish(false)
    }

    window.addEventListener('pointermove', onPointerMove, { passive: false })
    window.addEventListener('pointerover', onPointerOver)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
    window.addEventListener('keydown', onKeyDown)

    return () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerover', onPointerOver)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [attribute, rowAttribute, finish, place, step])

  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current)
      document.body.classList.remove('board-dragging')
    },
    [],
  )

  const begin = useCallback(
    (event: ReactPointerEvent, id: string, band: string) => {
      if (!enabled || event.button !== 0) return
      // A finger dragging a row is a finger scrolling the list. Leave touch to
      // the browser and let the row's own controls do the moving on a phone.
      if (event.pointerType === 'touch') return
      if ((event.target as HTMLElement | null)?.closest(CONTROLS)) return

      wasDrag.current = false
      pointer.current = { x: event.clientX, y: event.clientY }
      target.current = band
      insert.current = null
      gesture.current = {
        id,
        from: band,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        armed: false,
        scroller: nearestScroller(event.currentTarget as HTMLElement),
      }
    },
    [enabled],
  )

  const dragged = useCallback(() => wasDrag.current, [])

  return { begin, dragged, carrying, over, before, ghostRef }
}
